// SCR-002 view model: which sign-in state the page is in and what it says. The BFF and the app
// send the visitor here as `/signin?reason=…&returnTo=…`; nothing secret or personal travels in
// that URL — who is named on "no access" comes from the live session, never the query.
import { safeReturnTo, signInHref } from "../../session/session";

export type SignInReason = "expired" | "logged_out" | "no_access" | "issuer_unreachable";

export type SignInState =
  | { kind: "default"; returnTo: string }
  | { kind: "signing-in"; returnTo: string }
  | { kind: "no-access"; live: boolean; who: string | null }
  | { kind: "signed-out"; reason: "expired" | "logged_out"; returnTo: string }
  | { kind: "error"; returnTo: string };

export const SIGN_IN_PATH = "/signin";

/** Where to send a visitor who must sign in, remembering the page to come back to. */
export function signInPath(reason: SignInReason | null, returnTo: string | null): string {
  const query = new URLSearchParams();
  if (reason) query.set("reason", reason);
  const target = safeReturnTo(returnTo);
  if (target !== "/") query.set("returnTo", target);
  const qs = query.toString();
  return qs ? `${SIGN_IN_PATH}?${qs}` : SIGN_IN_PATH;
}

/** A live session that maps to no role, and who it belongs to (email or name; null if unknown). */
export interface NoAccessSession {
  who: string | null;
}

/** The page's state from its query, plus the live session that has no role, if any. */
export function signInStateFrom(query: URLSearchParams, noAccess: NoAccessSession | null): SignInState {
  const returnTo = safeReturnTo(query.get("returnTo"));
  const reason = query.get("reason");
  if (noAccess !== null) return { kind: "no-access", live: true, who: noAccess.who };
  if (reason === "no_access") return { kind: "no-access", live: false, who: null };
  if (reason === "expired" || reason === "logged_out") return { kind: "signed-out", reason, returnTo };
  if (reason === "issuer_unreachable") return { kind: "error", returnTo };
  return { kind: "default", returnTo };
}

/** Clicking "Sign in" leaves for the issuer; until the page unloads the button shows progress. */
export function startSigningIn(state: SignInState): SignInState {
  return "returnTo" in state ? { kind: "signing-in", returnTo: state.returnTo } : state;
}

export interface SignInView {
  heading: string;
  message: string | null;
  /** The sign-in link, absent when signing in cannot help (no access). */
  signIn: { label: string; href: string; busy: boolean } | null;
  /** Offer sign-out (the visitor holds a session that can reach nothing). */
  signOut: boolean;
  tone: "neutral" | "warning" | "error";
}

export function signInView(state: SignInState, issuerName: string): SignInView {
  const link = (returnTo: string, label: string, busy = false) => ({ label, href: signInHref(returnTo), busy });
  switch (state.kind) {
    case "default":
      return {
        heading: "Sign in to Nexus Admin",
        message: `Use your ${issuerName} account.`,
        signIn: link(state.returnTo, `Sign in with ${issuerName}`),
        signOut: false,
        tone: "neutral",
      };
    case "signing-in":
      return {
        heading: "Sign in to Nexus Admin",
        message: `Taking you to ${issuerName}…`,
        signIn: link(state.returnTo, "Signing in…", true),
        signOut: false,
        tone: "neutral",
      };
    case "no-access":
      return {
        heading: "No access",
        message: !state.live
          ? "Your account has no Nexus admin role."
          : state.who === null
            ? "You are signed in, but your account has no Nexus admin role."
            : `Signed in as ${state.who}, but your account has no Nexus admin role.`,
        signIn: null,
        signOut: state.live,
        tone: "warning",
      };
    case "signed-out":
      return {
        heading: "Signed out",
        message: state.reason === "expired" ? "Your session ended. Sign in again to continue where you were." : "You signed out.",
        signIn: link(state.returnTo, "Sign in again"),
        signOut: false,
        tone: "neutral",
      };
    case "error":
      return {
        heading: "Sign-in unavailable",
        message: `${issuerName} could not be reached. Try again in a moment.`,
        signIn: link(state.returnTo, "Try again"),
        signOut: false,
        tone: "error",
      };
  }
}
